/**
 * Landing — public homepage shown on `/` before login.
 *
 * Sections (single page, anchor-scrolled): #home, #about, #services,
 * #products, #partners, #stats (conditionally), #contact. The header
 * exposes a Language selector and a Login button (routes to /login,
 * or /dashboard if the visitor already has an active session).
 *
 * All statistics are driven by /api/branding.landing_stats — MASTER_ADMIN
 * toggles which numbers to publish from Branding Settings. Nothing is
 * displayed unless explicitly confirmed & enabled by the admin.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Globe2,
  Menu,
  X,
  Gift,
  Smartphone,
  PhoneCall,
  Code2,
  LayoutDashboard,
  TabletSmartphone,
  ArrowRight,
  Check,
  Shield,
  ShieldCheck,
  Zap,
  Users,
  MessageCircle,
  Mail,
  ChevronDown,
  Gauge,
  Tag,
  Sparkles,
  Headphones,
} from "lucide-react";
import { useBranding, whatsappHref } from "@/context/BrandingContext";
import { useLandingContent } from "@/context/LandingContentContext";
import { useAuth } from "@/context/AuthContext";
import { SUPPORTED_LANGUAGES, setStoredLanguage } from "@/i18n";
import FlagIcon from "@/components/ui/FlagIcon";
import SocialIcon from "@/components/ui/SocialIcon";
import ContactModal from "@/components/ContactModal";

// Public social + store URLs. Kept centralised so a single admin
// override later can flip them without touching JSX.
const SOCIAL_LINKS = [
  { name: "facebook",  url: "https://facebook.com/myglobaltopup",  label: "Facebook" },
  { name: "youtube",   url: "https://youtube.com/@myglobaltopup",  label: "YouTube" },
  { name: "tiktok",    url: "https://tiktok.com/@myglobaltopup",   label: "TikTok" },
  { name: "instagram", url: "https://instagram.com/myglobaltopup", label: "Instagram" },
  { name: "x",         url: "https://x.com/myglobaltopup",         label: "X" },
];

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.myglobaltopup.app";

const NAV_ITEMS = [
  { id: "home", key: "landing.nav_home", label_en: "Home" },
  { id: "about", key: "landing.nav_about", label_en: "About" },
  { id: "services", key: "landing.nav_services", label_en: "Services" },
  { id: "products", key: "landing.nav_products", label_en: "Products" },
  { id: "partners", key: "landing.nav_partners", label_en: "Partners" },
  // "Contact" scrolls to the "We speak with numbers" stats block, which
  // sits directly above the CTA / footer group — so the visitor gets a
  // full read of the closing sequence: stats → CTA → footer.
  { id: "stats", key: "landing.nav_contact", label_en: "Contact" },
];

const SERVICES = [
  {
    id: "gift-cards",
    icon: Gift,
    key: "landing.svc_giftcards",
    title_en: "Gift Cards",
    desc_en: "Digital gift cards for popular brands and platforms, delivered instantly.",
  },
  {
    id: "topup",
    icon: Smartphone,
    key: "landing.svc_topup",
    title_en: "Topup & Bill Payments",
    desc_en: "Instant mobile recharges and utility bill payments to hundreds of operators worldwide.",
  },
  {
    id: "international-calls",
    icon: PhoneCall,
    key: "landing.svc_intl_calls",
    title_en: "International Calls",
    desc_en: "We know times are tough right now — but cost should never keep you from staying in touch with your loved ones.",
  },
  {
    id: "api",
    icon: Code2,
    key: "landing.svc_api",
    title_en: "API Integration",
    desc_en: "REST API for businesses and resellers to natively integrate topups, cash-outs and gift cards.",
  },
];

const PRODUCTS = [
  {
    id: "web",
    icon: LayoutDashboard,
    key: "landing.prod_web",
    title_en: "Web Portal",
    desc_en: "Full-featured reseller dashboard: real-time wallet, instant topups, transparent commissions, ID-verified customers, receipts & reports.",
    bullets_en: [
      "Multi-role hierarchy — Master Agent · Agent · Collaborator",
      "USD-only wallets with instant wallet funding",
      "PDF receipts and audit-grade reports",
    ],
  },
  {
    id: "mobile",
    icon: TabletSmartphone,
    key: "landing.prod_mobile",
    title_en: "Mobile Application",
    desc_en: "Send topups, verify identities, approve cash-outs and monitor your agents from anywhere.",
    bullets_en: [
      "Native experience for agents on the go",
      "Push notifications for pending approvals",
      "Offline-safe receipts",
    ],
  },
];

// Operator brand cards — we display the operator name using each brand's
// characteristic color and typographic weight. We intentionally render
// custom CSS-styled wordmarks (not the copyrighted logo files) so that the
// section stays legally clean while still reading as instantly familiar to
// the visitor. The disclaimer below the grid reinforces trademark ownership.
const OPERATORS = [
  { id: "digicel",  name: "Digicel",  color: "#E60028", weight: 900, italic: false, tracking: "-0.03em" },
  { id: "natcom",   name: "natcom",   color: "#F58A2E", weight: 800, italic: false, tracking: "-0.02em" },
  { id: "claro",    name: "Claro",    color: "#DA291C", weight: 800, italic: false, tracking: "-0.02em", accent: "#FFDD00" },
  { id: "btc",      name: "BTC",      color: "#0072BC", weight: 900, italic: false, tracking: "-0.03em", tagline: "Everyday" },
  { id: "viva",     name: "viva",     color: "#82BC00", weight: 800, italic: true,  tracking: "-0.03em", tagline: "estamos de tu lado" },
  { id: "movistar", name: "movistar", color: "#019DF4", weight: 700, italic: false, tracking: "-0.03em" },
  { id: "telcel",   name: "telcel",   color: "#0068B7", weight: 800, italic: true,  tracking: "-0.02em" },
  { id: "entel",    name: "entel",    color: "#F16B00", weight: 700, italic: false, tracking: "-0.02em" },
];

function scrollTo(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/** Resolve the currently active i18n language from the CMS context (which
 * doesn't expose i18n directly). We stash it on `cms.updatedAt` — no, we
 * actually read from i18next through document.documentElement.lang if set,
 * else default to "en". */
function i18nLang() {
  try {
    if (typeof document !== "undefined" && document.documentElement.lang) {
      return document.documentElement.lang.split("-")[0];
    }
    if (typeof localStorage !== "undefined") {
      const stored = localStorage.getItem("globetopup.lang");
      if (stored) return stored;
    }
  } catch (_e) { /* SSR safety */ }
  return "en";
}

/** Idempotently write <meta> tags for the current SEO config. Removes any
 * previously-injected CMS tag before re-writing so language switches don't
 * accumulate stale tags. */
function injectSeoTags({ seo, lang, brand }) {
  if (typeof document === "undefined") return;
  document.querySelectorAll('meta[data-cms-seo="1"], link[data-cms-seo="1"]').forEach((n) => n.remove());
  const perLang = seo?.per_language?.[lang] || {};
  const title = perLang.title;
  const description = perLang.description;
  const keywords = perLang.keywords;
  const ogTitle = perLang.og_title || title;
  const ogDescription = perLang.og_description || description;
  const canonical = seo?.canonical_url;
  const allowIndex = seo?.allow_indexing !== false;

  const add = (tag, attrs) => {
    const el = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute("data-cms-seo", "1");
    document.head.appendChild(el);
  };
  if (description) add("meta", { name: "description", content: description });
  if (keywords) add("meta", { name: "keywords", content: keywords });
  if (ogTitle) add("meta", { property: "og:title", content: ogTitle });
  if (ogDescription) add("meta", { property: "og:description", content: ogDescription });
  add("meta", { property: "og:site_name", content: brand?.brand_name || "My GlobalTopup" });
  add("meta", { property: "og:type", content: "website" });
  if (canonical) {
    add("link", { rel: "canonical", href: canonical });
    add("meta", { property: "og:url", content: canonical });
  }
  add("meta", { name: "robots", content: allowIndex ? "index,follow" : "noindex,nofollow" });
}

function LanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];
  return (
    <div className="relative" data-testid="landing-language-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors"
        data-testid="landing-language-toggle"
      >
        <Globe2 className="w-4 h-4 text-slate-500" />
        <FlagIcon code={current.flag} />
        <span className="font-medium">{current.label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50"
          data-testid="landing-language-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setStoredLanguage(l.code); setOpen(false); }}
              className={`flex items-center gap-3 w-full px-3 py-2 text-sm text-left hover:bg-slate-50 ${
                l.code === current.code ? "bg-blue-50 text-[#0F62FE] font-semibold" : "text-slate-700"
              }`}
              data-testid={`landing-language-${l.code}`}
            >
              <FlagIcon code={l.flag} />
              <span className="flex-1">{l.label}</span>
              {l.code === current.code && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brand } = useBranding();
  const { user } = useAuth();
  const cms = useLandingContent();
  const cmsPartners = useMemo(
    () => (cms.partners || []).filter((p) => p.enabled !== false).sort((a, b) => (a.order || 0) - (b.order || 0)),
    [cms.partners],
  );
  const cmsToggles = cms.toggles || {};
  const [contactOpen, setContactOpen] = useState(false);
  const heroBg = cms.images?.hero_bg;
  const heroBgUrl = heroBg?.asset_id
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/landing-settings/asset/${heroBg.asset_id}`
    : null;
  const cmsDesign = cms.design || {};
  const cmsSeo = cms.seo || {};
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Ensure the browser tab reflects the branded name when arriving here.
    // If the SEO block has a per-language title, prefer it.
    const lang = i18nLang();
    const seoTitle = cmsSeo?.per_language?.[lang]?.title;
    if (typeof document !== "undefined") {
      document.title = seoTitle || brand?.brand_name || "My GlobalTopup";
    }
    injectSeoTags({ seo: cmsSeo, lang, brand });
  }, [brand?.brand_name, cmsSeo, cms.updatedAt, brand]);

  // Stats displayed in the "We speak with numbers" section. Values come from
  // the MASTER_ADMIN-configurable landing_stats (Branding Settings) with
  // sensible fallbacks; labels always come from i18n so the section
  // translates cleanly when the visitor switches language.
  const stats = useMemo(() => {
    if (brand?.landing_stats_section_enabled === false) return [];
    const defaults = {
      operators: { value: "400+", labelKey: "landing.stat_operators", icon: Smartphone },
      countries: { value: "70+",  labelKey: "landing.stat_countries", icon: Globe2 },
      users:     { value: "1M+",  labelKey: "landing.stat_users",     icon: Users },
      support:   { value: "24/7", labelKey: "landing.stat_support",   icon: Headphones },
    };
    const cfg = Object.fromEntries((brand?.landing_stats || []).map((s) => [s.id, s]));
    return Object.entries(defaults).map(([id, d]) => {
      const c = cfg[id];
      // A stat is on unless the admin explicitly disabled it.
      const enabled = c ? c.enabled !== false : true;
      if (!enabled) return null;
      const value = (c && c.value && c.value.trim()) || d.value;
      return { id, value, label: t(d.labelKey, id), Icon: d.icon };
    }).filter(Boolean);
  }, [brand, t]);

  const isAuthed = user && typeof user === "object" && user.id;
  const primaryCtaHref = isAuthed ? "/dashboard" : "/login";
  const primaryCtaLabel = isAuthed
    ? t("landing.cta_dashboard", "Go to Dashboard")
    : t("landing.cta_login", "Login");

  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* ------------------------------ Header ------------------------------ */}
      <header
        className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200"
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={() => scrollTo("home")}
            className="flex items-center gap-2"
            data-testid="landing-brand"
          >
            <div className="w-9 h-9 rounded-lg bg-[#0F62FE] text-white flex items-center justify-center overflow-hidden">
              {brand.brand_logo_url ? (
                <img src={brand.brand_logo_url} alt="logo" className="w-full h-full object-contain" />
              ) : (
                <Globe2 className="w-5 h-5" strokeWidth={2.2} />
              )}
            </div>
            <div className="font-bold text-base sm:text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {brand.brand_name}
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-1" data-testid="landing-nav">
            {NAV_ITEMS.map((it) => (
              <button
                key={it.id}
                type="button"
                onClick={() => scrollTo(it.id)}
                className="px-3 h-9 rounded-md text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                data-testid={`landing-nav-${it.id}`}
              >
                {t(it.key, it.label_en)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <LanguagePicker />
            </div>
            <Link
              to={primaryCtaHref}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] transition-colors"
              data-testid="landing-login-btn"
            >
              {primaryCtaLabel}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((o) => !o)}
              className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              data-testid="landing-menu-toggle"
              aria-label="Open menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="landing-mobile-menu">
            <div className="px-4 py-3 flex flex-col gap-1">
              {NAV_ITEMS.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => { scrollTo(it.id); setMobileOpen(false); }}
                  className="text-left px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
                  data-testid={`landing-mnav-${it.id}`}
                >
                  {t(it.key, it.label_en)}
                </button>
              ))}
              <div className="pt-2 border-t border-slate-100 mt-2">
                <LanguagePicker />
              </div>
            </div>
          </div>
        )}
      </header>

      {/* --------------------------- Promo / Announcement bar ----------------
          Shown only when the CMS `promo_ticker` toggle is on AND the
          `landing.promo_text` translation is non-empty for the current
          language. Renders a slowly scrolling marquee — same content
          duplicated twice with a CSS `animation: marquee` for seamless
          looping. Hidden entirely when off/empty so no space is reserved. */}
      {cmsToggles.promo_ticker && (t("landing.promo_text", "") || "").trim() && (
        <div
          className="relative bg-gradient-to-r from-[#0F62FE] via-[#0050E6] to-[#0F62FE] text-white overflow-hidden border-b border-white/10"
          data-testid="landing-promo-ticker"
          role="marquee"
          aria-label={t("landing.promo_text", "")}
        >
          <div className="relative flex overflow-hidden py-2">
            <div className="landing-promo-track flex whitespace-nowrap will-change-transform">
              {[0, 1].map((n) => (
                <div
                  key={n}
                  className="flex items-center gap-8 px-6 shrink-0 text-sm font-semibold tracking-wide"
                  aria-hidden={n === 1 ? "true" : undefined}
                >
                  {t("landing.promo_text", "")
                    .split("·")
                    .map((chunk, i) => (
                      <span key={i} className="inline-flex items-center gap-8">
                        <span className="text-white/70">◆</span>
                        <span>{chunk.trim()}</span>
                      </span>
                    ))}
                  <span className="text-white/70">◆</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------ Hero -------------------------------- */}
      <section
        id="home"
        className="relative overflow-hidden text-white"
        data-testid="landing-hero"
      >
        {/* Full-bleed hero backdrop: photorealistic couple with globe / world-
            map pattern, generated clean (no text baked in). The image itself
            provides the background — we don't need any gradient behind it. */}
        <img
          src={heroBgUrl || "/landing-hero-wide.jpg"}
          alt={heroBg?.alt || ""}
          aria-hidden={heroBg?.alt ? undefined : "true"}
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit: heroBg?.object_fit || "cover",
            objectPosition: heroBg?.object_position || "right center",
          }}
          data-testid="landing-hero-image"
        />
        {/* Left-side readability overlay: fades the deep blue on the left half
            so the headline stays crisp without hiding the couple on the right.
            On mobile the overlay is a straight vertical dim so text remains
            readable when the image auto-crops. Colour and opacity are
            controllable through Landing Settings → Design. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(to right, ${cmsDesign.hero_overlay_color || "#001A66"} 0%, ${cmsDesign.hero_overlay_color || "#001A66"}${Math.round(((cmsDesign.hero_overlay_opacity ?? 0.55) * 0.65) * 255).toString(16).padStart(2, "0")} 50%, transparent 100%)`,
            opacity: cmsDesign.hero_overlay_opacity != null ? Math.min(1, cmsDesign.hero_overlay_opacity + 0.35) : 1,
          }}
        />
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#001A66]/40 via-transparent to-[#001A66]/20 sm:hidden" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
              <Zap className="w-3.5 h-3.5" /> {t("landing.hero_kicker", "Instant · Secure · Global")}
            </div>
            <h1
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] drop-shadow-md"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="landing-hero-title"
            >
              {t("landing.hero_title", "Connect the world with {{brand}}", { brand: brand.brand_name })}
            </h1>
            <p className="mt-5 text-lg text-white/90 max-w-xl drop-shadow" data-testid="landing-hero-subtitle">
              {t(
                "landing.hero_subtitle",
                "A simple, fast and secure platform for international mobile topups, digital payments and reseller network management."
              )}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate(primaryCtaHref)}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-white text-[#0F62FE] font-bold hover:bg-slate-100 transition-colors shadow-lg"
                data-testid="landing-hero-cta-primary"
              >
                {primaryCtaLabel} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollTo("services")}
                className="inline-flex items-center gap-2 h-12 px-6 rounded-lg border border-white/40 bg-white/10 backdrop-blur text-white font-semibold hover:bg-white/20 transition-colors"
                data-testid="landing-hero-cta-secondary"
              >
                {t("landing.hero_cta_learn", "Explore services")}
              </button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/90">
              <span className="inline-flex items-center gap-1.5"><Shield className="w-4 h-4" /> {t("landing.hero_trust_secure", "Bank-grade encryption")}</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="w-4 h-4" /> {t("landing.hero_trust_instant", "Real-time settlement")}</span>
              <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {t("landing.hero_trust_multi", "Multi-role network")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------ Stats ------------------------------- */}
      {/* The premium stats section ("We speak with numbers") lives further
          down the page between Products and Partners — see id="stats" below. */}

      {/* ------------------------------ About ------------------------------- */}
      <section
        id="about"
        className="relative py-20 sm:py-28 bg-white overflow-hidden"
        data-testid="landing-about"
      >
        {/* Subtle globe / dotted-map graphic in the bottom-left corner —
            decorative background inspired by the reference design. */}
        <div
          className="pointer-events-none absolute -bottom-32 -left-32 w-[520px] h-[520px] opacity-[0.10]"
          aria-hidden="true"
        >
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full text-[#0F62FE]">
            <defs>
              <radialGradient id="aboutGlobeGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
                <stop offset="70%" stopColor="currentColor" stopOpacity="0.3" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="100" cy="100" r="80" fill="url(#aboutGlobeGrad)" />
            {/* Longitude lines */}
            {[0, 30, 60, 90, 120, 150].map((r) => (
              <ellipse
                key={r}
                cx="100"
                cy="100"
                rx={80 * Math.abs(Math.cos((r * Math.PI) / 180))}
                ry="80"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
                opacity="0.6"
              />
            ))}
            {/* Latitude lines */}
            {[-60, -30, 0, 30, 60].map((y) => (
              <ellipse
                key={y}
                cx="100"
                cy={100 + y * 0.9}
                rx={80 * Math.cos((y * Math.PI) / 180)}
                ry="4"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.6"
                opacity="0.6"
              />
            ))}
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          {/* ---- Left column: kicker + title (with blue glow) + paragraphs ---- */}
          <div>
            <div
              className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F62FE]"
              data-testid="landing-about-kicker"
            >
              {t("landing.about_kicker", "Acerca de")}
            </div>
            <div className="relative mt-3">
              {/* Blue glow behind the title block */}
              <div
                className="pointer-events-none absolute -inset-4 rounded-3xl bg-[#0F62FE]/10 blur-xl"
                aria-hidden="true"
              />
              <h2
                className="relative rounded-3xl border-2 border-[#0F62FE]/40 bg-white/70 backdrop-blur px-6 py-5 text-4xl sm:text-[2.5rem] lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 leading-[1.1] shadow-[0_0_60px_-15px_rgba(15,98,254,0.35)]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid="landing-about-title"
              >
                {t("landing.about_title", "Diseñado para revendedores, elegido por agentes.")}
              </h2>
              {/* Blue underline accent */}
              <div className="mt-4 h-1 w-16 rounded-full bg-[#0F62FE]" />
            </div>

            <p
              className="mt-7 text-slate-600 text-base sm:text-lg leading-relaxed"
              data-testid="landing-about-p1"
            >
              {t("landing.about_p1_prefix", "{{brand}} es una plataforma global de recargas diseñada para ", { brand: brand.brand_name })}
              <span className="font-semibold text-[#0F62FE] underline decoration-[#0F62FE]/40 decoration-2 underline-offset-4">
                {t("landing.about_p1_role1", "master agente")}
              </span>
              {t("landing.about_p1_between", " y ")}
              <span className="font-semibold text-[#0F62FE] underline decoration-[#0F62FE]/40 decoration-2 underline-offset-4">
                {t("landing.about_p1_role2", "agente de campo")}
              </span>
              {t(
                "landing.about_p1_suffix",
                ". Te damos la columna operacional — cartera, libro mayor, comprobantes y cumplimiento — para que tú te concentres en atender a tus clientes."
              )}
            </p>
            <p
              className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed"
              data-testid="landing-about-p2"
            >
              {t(
                "landing.about_p2",
                "Desde operaciones en un solo país hasta redes multi-región, nuestras herramientas escalan contigo: comisiones transparentes, analítica en tiempo real y auditabilidad de nivel empresarial listas para usar."
              )}
            </p>
          </div>

          {/* ---- Right column: 5 feature cards in a 2 (top) + 3 (bottom) grid --- */}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-4" data-testid="landing-about-cards">
            {[
              { id: "secure",     icon: ShieldCheck, span: "sm:col-span-3", key: "landing.value_secure",     title_en: "Seguro por diseño", desc_en: "2FA, dispositivos de confianza, auditoría en cada acción." },
              { id: "realtime",   icon: Gauge,       span: "sm:col-span-3", key: "landing.value_realtime",   title_en: "Tiempo real",       desc_en: "Cartera, libro mayor y comprobantes al instante." },
              { id: "multirole",  icon: Users,       span: "sm:col-span-2", key: "landing.value_hierarchy",  title_en: "Multi-rol",         desc_en: "Master agente · Agente · Colaborador." },
              { id: "global",     icon: Globe2,      span: "sm:col-span-2", key: "landing.value_global",     title_en: "Global",            desc_en: "Multi-moneda, multi-país, carteras ancladas en USD." },
              { id: "whitelabel", icon: Tag,         span: "sm:col-span-2", key: "landing.value_whitelabel", title_en: "White Label",       desc_en: "Marca propia, personalización y experiencia lista para tu negocio." },
            ].map((v) => (
              <FeatureCard
                key={v.id}
                testId={`landing-value-${v.id}`}
                className={v.span}
                Icon={v.icon}
                title={t(`${v.key}_title`, v.title_en)}
                description={t(`${v.key}_desc`, v.desc_en)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------ Services ---------------------------- */}
      <section
        id="services"
        className="relative py-20 sm:py-28 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden"
        data-testid="landing-services"
      >
        {/* Soft radial blue glow at the top-right for the premium accent */}
        <div
          className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#0F62FE]/10 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F62FE]">
              {t("landing.services_kicker", "Services")}
            </div>
            <h2
              className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("landing.services_title", "Everything your reseller network needs.")}
            </h2>
            <div className="mt-4 h-1 w-16 rounded-full bg-[#0F62FE]" />
            <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed">
              {t("landing.services_sub", "Four core services — one integrated platform, one wallet, one dashboard.")}
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <FeatureCard
                key={s.id}
                testId={`landing-service-${s.id}`}
                Icon={s.icon}
                title={t(`${s.key}_title`, s.title_en)}
                description={t(`${s.key}_desc`, s.desc_en)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------ Products ---------------------------- */}
      <section
        id="products"
        className="relative py-20 sm:py-28 bg-white overflow-hidden"
        data-testid="landing-products"
      >
        {/* Left soft blue radial accent */}
        <div
          className="pointer-events-none absolute -top-32 -left-40 w-[520px] h-[520px] rounded-full bg-[#0F62FE]/8 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F62FE]">
              {t("landing.products_kicker", "Products")}
            </div>
            <h2
              className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("landing.products_title", "Two ways to run your business.")}
            </h2>
            <div className="mt-4 h-1 w-16 rounded-full bg-[#0F62FE]" />
          </div>

          {/* --- Web Portal card --- */}
          <ProductCard
            testId="landing-product-web"
            Icon={LayoutDashboard}
            title={t("landing.prod_web_title", "Web Portal")}
            description={t("landing.prod_web_desc", PRODUCTS[0].desc_en)}
            bullets={PRODUCTS[0].bullets_en.map((b, i) => t(`landing.prod_web_bullet_${i}`, b))}
            className="mt-14"
          >
            {/* Layered portal screenshots — Master Agent (front) + Agent (back) */}
            <div className="relative w-full aspect-[16/11]">
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-tr from-[#0F62FE]/15 to-transparent rounded-2xl"
              />
              {/* Back screenshot — Agent portal, offset */}
              <div className="absolute top-0 right-0 w-[72%] rounded-xl overflow-hidden shadow-xl ring-1 ring-slate-200/70 rotate-2">
                <img
                  src="/product-agent-portal.png"
                  alt={t("landing.prod_web_agent_alt", "Agent portal screenshot")}
                  className="w-full h-auto block"
                  data-testid="landing-product-agent-shot"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] shadow-sm">
                  <Users className="w-3 h-3" /> {t("landing.prod_web_agent_label", "Agent")}
                </div>
              </div>
              {/* Front screenshot — Master Agent portal */}
              <div className="absolute bottom-0 left-0 w-[78%] rounded-xl overflow-hidden shadow-2xl ring-1 ring-slate-300/70 -rotate-1">
                <img
                  src="/product-master-agent-portal.png"
                  alt={t("landing.prod_web_master_alt", "Master Agent portal screenshot")}
                  className="w-full h-auto block"
                  data-testid="landing-product-master-agent-shot"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[#0F62FE] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white shadow-sm">
                  <ShieldCheck className="w-3 h-3" /> {t("landing.prod_web_master_label", "Master Agent")}
                </div>
              </div>
            </div>
          </ProductCard>

          {/* --- Mobile Application card --- */}
          <ProductCard
            testId="landing-product-mobile"
            Icon={TabletSmartphone}
            title={t("landing.prod_mobile_title", "Mobile Application")}
            description={t("landing.prod_mobile_desc", PRODUCTS[1].desc_en)}
            bullets={PRODUCTS[1].bullets_en.map((b, i) => t(`landing.prod_mobile_bullet_${i}`, b))}
            reverse
            className="mt-10"
          >
            {/* Three-phones mockup — sample of the future mobile app UI */}
            <div className="relative w-full">
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-gradient-to-tl from-[#0F62FE]/15 to-transparent rounded-2xl"
              />
              <img
                src="/product-mobile-mockup.png"
                alt={t("landing.prod_mobile_alt", "Future My GlobalTopup mobile app mockup")}
                className="relative w-full h-auto block rounded-2xl"
                data-testid="landing-product-mobile-mockup"
                loading="lazy"
              />
            </div>
          </ProductCard>
        </div>
      </section>

      {/* ------------------------------ Partners ---------------------------- */}
      <section
        id="partners"
        className="relative py-20 sm:py-24 bg-slate-50 overflow-hidden"
        data-testid="landing-partners"
      >
        {/* Soft top-right blue accent, consistent with Services section */}
        <div
          className="pointer-events-none absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#0F62FE]/8 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0F62FE]">
              {t("landing.partners_kicker", "Services available")}
            </div>
            <h2
              className="mt-3 text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("landing.partners_title", "Send topups to leading operators.")}
            </h2>
            <div className="mt-4 mx-auto h-1 w-16 rounded-full bg-[#0F62FE]" />
            <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed">
              {t(
                "landing.partners_sub",
                "Below is a sample of the operators available through our platform. Ask our support team for the full list — hundreds of networks across dozens of countries."
              )}
            </p>
          </div>
          <div
            className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5"
            data-testid="landing-partners-grid"
          >
            {(cmsPartners.length ? cmsPartners : OPERATORS).map((op) => (
              <div
                key={op.id}
                className="group relative aspect-[16/10] rounded-2xl bg-white border border-slate-200 hover:border-[#0F62FE]/25 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_40px_-20px_rgba(15,98,254,0.35)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center overflow-hidden"
                data-testid={`landing-operator-${op.id}`}
              >
                {/* Subtle brand-color accent bar on top */}
                <div
                  aria-hidden="true"
                  className="absolute top-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: op.color || op.brand_color || "#0F62FE" }}
                />
                {op.logo_asset_id && op.has_logo_rights ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/landing-settings/asset/${op.logo_asset_id}`}
                    alt={op.name}
                    className="max-h-16 max-w-[75%] object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center">
                    <div
                      className="text-3xl sm:text-4xl leading-none"
                      style={{
                        color: op.color || op.brand_color || "#0F62FE",
                        fontWeight: op.weight || 800,
                        fontStyle: op.italic ? "italic" : "normal",
                        letterSpacing: op.tracking || "-0.02em",
                        fontFamily: "'Cabinet Grotesk', 'Inter', sans-serif",
                      }}
                    >
                      {op.name}
                      {op.accent && (
                        <span
                          className="ml-0.5 inline-block align-top"
                          style={{ color: op.accent, fontSize: "0.6em", transform: "translateY(-4px)" }}
                        >
                          ‹
                        </span>
                      )}
                    </div>
                    {op.tagline && (
                      <div
                        className="mt-1 text-[10px] uppercase tracking-widest"
                        style={{ color: op.color || op.brand_color, opacity: 0.85 }}
                      >
                        {op.tagline}
                      </div>
                    )}
                    {op.country && (
                      <div className="mt-1 text-[9px] uppercase tracking-widest text-slate-400">
                        {op.country}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="mt-12 text-[11px] text-slate-500 text-center max-w-3xl mx-auto italic">
            {t(
              "landing.partners_disclaimer",
              "All brand names, trademarks and logos mentioned are the property of their respective owners and are shown solely to identify the services available through our platform."
            )}
          </p>
        </div>
      </section>

      {/* -------------------- We speak with numbers ------------------------
          Moved to just before the CTA so the "Contact" nav link scrolls to
          this stats section first, then the compact blue CTA, then the
          footer — matches the requested bottom-of-page reading order. */}
      {stats.length > 0 && (
        <section
          id="stats"
          className="relative pt-20 pb-8 sm:pt-24 sm:pb-10 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden"
          data-testid="landing-stats"
        >
          {/* Floating decorative shapes — small dots + rotated squares in the
              brand's blue palette, echoing the reference layout. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <span className="absolute top-16 left-[18%] w-2 h-2 rounded-full bg-[#0F62FE]/60" />
            <span className="absolute top-24 right-[22%] w-1.5 h-1.5 rounded-full bg-[#4098FF]/60" />
            <span className="absolute bottom-24 left-[28%] w-2.5 h-2.5 rounded-full bg-[#0038B8]/40" />
            <span className="absolute top-40 left-[46%] w-2 h-2 rotate-45 bg-[#0F62FE]/25" />
            <span className="absolute bottom-16 left-[38%] w-3 h-3 rotate-45 bg-[#4098FF]/30" />
            <span className="absolute bottom-20 right-[16%] w-2 h-2 rotate-45 bg-[#0F62FE]/40" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-center text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="landing-stats-title"
            >
              {t("landing.stats_title", "We speak with numbers")}
            </h2>

            <div className={`mt-12 grid gap-6 sm:gap-8 ${
              stats.length === 1 ? "grid-cols-1 max-w-xs mx-auto" :
              stats.length === 2 ? "grid-cols-2 max-w-2xl mx-auto" :
              stats.length === 3 ? "grid-cols-2 md:grid-cols-3 max-w-4xl mx-auto" :
              "grid-cols-2 md:grid-cols-4"
            }`}>
              {stats.map((s) => (
                <div
                  key={s.id}
                  className="flex flex-col items-center text-center group"
                  data-testid={`landing-stat-${s.id}`}
                >
                  <div className="relative">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 rounded-2xl bg-[#0F62FE]/20 blur-xl"
                    />
                    <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-[#E9EFFF] to-[#DCE7FF] flex items-center justify-center ring-1 ring-[#0F62FE]/15 shadow-sm group-hover:scale-105 transition-transform">
                      <s.Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#0F62FE]" strokeWidth={2} />
                    </div>
                  </div>
                  <div
                    className="mt-4 text-4xl sm:text-5xl font-extrabold text-[#0F1B45] tracking-tight"
                    style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                  >
                    {s.value}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------ Contact / CTA ------------------------
          Compact variant: narrower outer container (max-w-3xl), lighter
          vertical padding on both the section wrapper and the blue card
          — brings the CTA closer to the footer while keeping typography,
          buttons and internal layout untouched (per spec §3). */}
      <section
        id="contact"
        className="relative pt-8 pb-10 sm:pt-10 sm:pb-14 bg-white overflow-hidden"
        data-testid="landing-contact"
      >
        {/* Subtle full-width globe / dotted-map graphic behind the CTA block */}
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.06]"
          aria-hidden="true"
        >
          <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-full max-w-5xl text-[#0F62FE]">
            <defs>
              <radialGradient id="ctaGlobeGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.9" />
                <stop offset="70%" stopColor="currentColor" stopOpacity="0.3" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="200" cy="100" r="90" fill="url(#ctaGlobeGrad)" />
            {[0, 30, 60, 90, 120, 150].map((r) => (
              <ellipse
                key={r}
                cx="200" cy="100"
                rx={90 * Math.abs(Math.cos((r * Math.PI) / 180))}
                ry="90"
                fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6"
              />
            ))}
            {[-60, -30, 0, 30, 60].map((y) => (
              <ellipse
                key={y}
                cx="200" cy={100 + y * 0.9}
                rx={90 * Math.cos((y * Math.PI) / 180)}
                ry="4"
                fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6"
              />
            ))}
          </svg>
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[1.75rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(15,98,254,0.55),0_0_0_1px_rgba(15,98,254,0.15)]">
            {/* Deep-blue gradient with soft radial highlights */}
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-br from-[#0F62FE] via-[#0038B8] to-[#001A66]"
            />
            <div
              aria-hidden="true"
              className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-24 -left-20 w-80 h-80 rounded-full bg-[#4098FF]/25 blur-3xl"
            />
            {/* Faint dotted globe overlay on the block itself */}
            <svg
              viewBox="0 0 400 200"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute inset-0 w-full h-full opacity-15 pointer-events-none"
              aria-hidden="true"
            >
              {[-60, -30, 0, 30, 60].map((y) => (
                <ellipse
                  key={y}
                  cx="200" cy={100 + y * 0.9}
                  rx={110 * Math.cos((y * Math.PI) / 180)}
                  ry="5"
                  fill="none" stroke="white" strokeWidth="0.6" opacity="0.7"
                />
              ))}
              {[0, 30, 60, 90, 120, 150].map((r) => (
                <ellipse
                  key={r}
                  cx="200" cy="100"
                  rx={110 * Math.abs(Math.cos((r * Math.PI) / 180))}
                  ry="110"
                  fill="none" stroke="white" strokeWidth="0.6" opacity="0.7"
                />
              ))}
            </svg>

            <div className="relative px-6 sm:px-10 py-10 sm:py-12 text-center text-white">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
                <Sparkles className="w-3.5 h-3.5" /> {t("landing.cta_kicker", "Start today")}
              </div>
              <h2
                className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                {t("landing.cta_title", "Ready to start reselling?")}
              </h2>
              <p className="mt-4 text-white/85 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
                {t("landing.cta_sub", "Sign in to your account or contact our team to become a partner.")}
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-3">
                <Link
                  to={primaryCtaHref}
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-[#0F62FE] font-bold hover:bg-slate-100 hover:-translate-y-0.5 hover:shadow-2xl transition-all shadow-lg"
                  data-testid="landing-contact-cta"
                >
                  {primaryCtaLabel} <ArrowRight className="w-4 h-4" />
                </Link>
                {brand.whatsapp_number && (
                  <a
                    href={whatsappHref(brand.whatsapp_number, `Hello ${brand.brand_name}, I'd like to learn more.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-white/30 bg-white/10 backdrop-blur text-white font-semibold hover:bg-white/20 hover:-translate-y-0.5 transition-all"
                    data-testid="landing-whatsapp-cta"
                  >
                    <MessageCircle className="w-4 h-4" /> {t("landing.cta_whatsapp", "Chat on WhatsApp")}
                  </a>
                )}
                {/* Send Email — hidden when the CMS toggle is off. Opens
                    the ContactModal in the current UI language. Never
                    exposes the destination address to the visitor. */}
                {cmsToggles.email_button !== false && (
                  <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-white/30 bg-white/10 backdrop-blur text-white font-semibold hover:bg-white/20 hover:-translate-y-0.5 transition-all"
                    data-testid="landing-email-cta"
                  >
                    <Mail className="w-4 h-4" /> {t("landing.cta_email_label", "Send Email")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------ Footer ------------------------------- */}
      <footer
        className="relative bg-gradient-to-b from-[#0B1425] to-[#050912] text-white pt-16 pb-8 overflow-hidden"
        data-testid="landing-footer"
      >
        {/* Subtle radial blue accent bottom-left */}
        <div
          className="pointer-events-none absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0F62FE]/15 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10">
            {/* Brand + tagline + download + socials */}
            <div className="col-span-2 md:col-span-3">
              <div className="flex items-center gap-2.5">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#4098FF] via-[#0F62FE] to-[#0038B8] flex items-center justify-center overflow-hidden shadow-lg">
                  {brand.brand_logo_url ? (
                    <img src={brand.brand_logo_url} alt={brand.brand_name} className="w-full h-full object-contain" />
                  ) : (
                    <Globe2 className="w-6 h-6 text-white" strokeWidth={2.2} />
                  )}
                </div>
                <div
                  className="font-bold text-xl text-white"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {brand.brand_name}
                </div>
              </div>
              <p className="mt-4 text-sm text-white/70 max-w-md leading-relaxed">
                {t("landing.footer_tagline", "A simple, fast and secure platform for global topups and digital payments.")}
              </p>

              {/* Google Play / App Store badges (respect CMS toggles) */}
              {(() => {
                const gp = cms.appStores?.google_play || { enabled: true, coming_soon: true, url: "" };
                const ap = cms.appStores?.app_store   || { enabled: false, coming_soon: true, url: "" };
                if (gp.enabled === false && ap.enabled === false) return null;
                return (
                  <div className="mt-6">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-2.5">
                      {t("landing.footer_download", "Download the app")}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {gp.enabled !== false && (
                        <a
                          href={gp.coming_soon ? undefined : (gp.url || PLAY_STORE_URL)}
                          target={gp.coming_soon ? undefined : "_blank"}
                          rel={gp.coming_soon ? undefined : "noreferrer"}
                          onClick={(e) => { if (gp.coming_soon) e.preventDefault(); }}
                          className={`inline-flex items-center gap-3 h-14 px-5 rounded-xl bg-black border border-white/15 hover:border-white/30 hover:bg-black/80 transition-all shadow-lg ${gp.coming_soon ? "cursor-default" : ""}`}
                          data-testid="landing-google-play"
                        >
                          <PlayStoreLogo className="w-7 h-7" />
                          <div className="text-left leading-tight">
                            <div className="text-[10px] uppercase tracking-widest text-white/70">
                              {gp.coming_soon ? t("landing.coming_soon", "Coming Soon") : t("landing.footer_play_get", "Get it on")}
                            </div>
                            <div className="text-base font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                              Google Play
                            </div>
                          </div>
                        </a>
                      )}
                      {ap.enabled !== false && (
                        <a
                          href={ap.coming_soon ? undefined : ap.url}
                          target={ap.coming_soon ? undefined : "_blank"}
                          rel={ap.coming_soon ? undefined : "noreferrer"}
                          onClick={(e) => { if (ap.coming_soon || !ap.url) e.preventDefault(); }}
                          className={`inline-flex items-center gap-3 h-14 px-5 rounded-xl bg-black border border-white/15 hover:border-white/30 hover:bg-black/80 transition-all shadow-lg ${ap.coming_soon ? "cursor-default" : ""}`}
                          data-testid="landing-app-store"
                        >
                          <div className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center text-white font-bold text-xs"></div>
                          <div className="text-left leading-tight">
                            <div className="text-[10px] uppercase tracking-widest text-white/70">
                              {ap.coming_soon ? t("landing.coming_soon", "Coming Soon") : t("landing.footer_play_get_apple", "Download on the")}
                            </div>
                            <div className="text-base font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                              App Store
                            </div>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Socials — prefer CMS-configured links when any are enabled */}
              {(() => {
                const cmsSocials = Object.entries(cms.social || {})
                  .filter(([, v]) => v?.enabled && v?.url)
                  .map(([name, v]) => ({ name, url: v.url, label: name }));
                const list = cmsSocials.length ? cmsSocials : SOCIAL_LINKS;
                if (!list.length) return null;
                return (
                  <div className="mt-8">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-3">
                      {t("landing.footer_follow", "Follow us")}
                    </div>
                    <div className="flex items-center gap-2.5" data-testid="landing-socials">
                      {list.map((s) => (
                        <a
                          key={s.name}
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={s.label}
                          title={s.label}
                          data-testid={`landing-social-${s.name}`}
                          className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#0F62FE] text-white/85 hover:text-white flex items-center justify-center transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                          <SocialIcon name={s.name} className="w-4 h-4" />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Nav columns */}
            <FooterCol
              title={t("landing.footer_company", "Company")}
              items={[
                { label: t("landing.nav_about", "About"), onClick: () => scrollTo("about") },
                { label: t("landing.nav_services", "Services"), onClick: () => scrollTo("services") },
                { label: t("landing.nav_products", "Products"), onClick: () => scrollTo("products") },
                { label: t("landing.nav_contact", "Contact"), onClick: () => scrollTo("stats") },
              ]}
              testid="footer-company"
            />
            <FooterCol
              title={t("landing.footer_products", "Products")}
              items={PRODUCTS.map((p) => ({ label: t(`${p.key}_title`, p.title_en), onClick: () => scrollTo("products") }))}
              testid="footer-products"
            />
            <FooterCol
              title={t("landing.footer_legal", "Legal")}
              items={[
                { label: t("landing.footer_privacy", "Privacy Policy"), to: "/privacy" },
                { label: t("landing.footer_terms", "Terms & Conditions"), to: "/terms" },
              ]}
              testid="footer-legal"
            />
          </div>

          {/* Bottom bar */}
          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-white">
            <div data-testid="footer-copyright">
              © {new Date().getFullYear()} {brand.brand_name}. {t("landing.footer_rights", "All rights reserved.")}
            </div>
            <div className="flex items-center gap-5">
              <Link
                to="/login"
                className="text-white hover:text-white/80 transition-colors"
                data-testid="footer-login"
              >
                {t("landing.footer_login", "Login")}
              </Link>
              <span className="text-white/30">·</span>
              <Link
                to="/forgot-password"
                className="text-white hover:text-white/80 transition-colors"
                data-testid="footer-forgot"
              >
                {t("landing.footer_forgot", "Forgot password")}
              </Link>
            </div>
          </div>
        </div>
      </footer>
      {/* Contact form — mounted at the root so the modal backdrop can
          escape any parent stacking context. Only renders itself when
          `contactOpen` is true; otherwise contributes no DOM. */}
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

/**
 * PlayStoreLogo — SVG mimicking the Play store multi-color triangle. Custom
 * artwork, drawn from scratch, no proprietary asset file included.
 */
function PlayStoreLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="pl-a" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#00A0FF" />
          <stop offset="1" stopColor="#00E5FF" />
        </linearGradient>
        <linearGradient id="pl-b" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFCE00" />
          <stop offset="1" stopColor="#FFBB00" />
        </linearGradient>
        <linearGradient id="pl-c" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF3A44" />
          <stop offset="1" stopColor="#C31162" />
        </linearGradient>
        <linearGradient id="pl-d" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#32A071" />
          <stop offset="1" stopColor="#00CC5F" />
        </linearGradient>
      </defs>
      <path d="M3 2.2v19.6c0 .45.24.86.62 1.09L14.5 12 3.62 1.11A1.27 1.27 0 0 0 3 2.2z" fill="url(#pl-a)" />
      <path d="M18.03 8.36 15.36 6.83 12.6 9.6 14.5 12l1.9-2.4 2.28-1.24z" transform="translate(0 0)" fill="url(#pl-c)" />
      <path d="M18.03 15.64l-2.63-1.24L14.5 12l-1.9 2.4 2.76 2.77 2.67-1.53z" fill="url(#pl-b)" />
      <path d="M3.62 22.89 14.5 12l-11.88 10.9c.3.19.68.22.99.05z" fill="url(#pl-d)" />
      <path d="M20.65 10.85 18.03 9.35v5.3l2.62-1.5c1.2-.69 1.2-2.28 0-2.97-.13-.07-2.62-1.5 0 .67z" fill="#000" opacity="0" />
      <path d="M20.6 10.85l-2.57-1.5v.02L20.4 11.4c1.16.66 1.16 2.53 0 3.2l-2.37 1.35v.02l2.57-1.5c1.2-.7 1.2-2.9 0-3.62z" fill="#0067A0" />
    </svg>
  );
}

function FooterCol({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-widest text-white">{title}</div>
      <ul className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <li key={i}>
            {it.to ? (
              <Link
                to={it.to}
                className="text-sm text-white/65 hover:text-white transition-colors"
              >
                {it.label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={it.onClick}
                className="text-sm text-white/65 hover:text-white transition-colors text-left"
              >
                {it.label}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * FeatureCard — premium About-section card with a gradient blue icon badge,
 * subtle blue glow, and hover lift. Matches the reference design language.
 */
function FeatureCard({ Icon, title, description, testId, className = "" }) {
  return (
    <div
      data-testid={testId}
      className={`group relative rounded-2xl bg-white border border-[#0F62FE]/20 p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_0_0_1px_rgba(15,98,254,0.05),0_10px_30px_-15px_rgba(15,98,254,0.35)] hover:shadow-[0_1px_2px_rgba(15,23,42,0.06),0_0_0_1px_rgba(15,98,254,0.15),0_20px_45px_-20px_rgba(15,98,254,0.55)] hover:-translate-y-0.5 transition-all duration-300 ${className}`}
    >
      {/* Soft blue halo behind the icon */}
      <div className="relative mx-auto w-16 h-16 sm:w-[70px] sm:h-[70px]">
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-[#0F62FE]/20 blur-2xl"
        />
        <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[#4098FF] via-[#0F62FE] to-[#0038B8] flex items-center justify-center shadow-lg ring-4 ring-white">
          <Icon
            className="w-7 h-7 sm:w-8 sm:h-8 text-white"
            strokeWidth={2}
          />
        </div>
      </div>
      <div className="mt-4 sm:mt-5 text-center">
        <h3
          className="text-lg sm:text-xl font-bold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
}

/**
 * ProductCard — large split card used in the Products section.
 * Text (kicker icon + title + description + bullets) on one side, image
 * children on the other. `reverse` swaps the two on desktop; on mobile the
 * layout always stacks (image first, text second) so the visual anchors the
 * message.
 */
function ProductCard({
  Icon,
  title,
  description,
  bullets,
  children,
  testId,
  reverse = false,
  comingSoonLabel,
  className = "",
}) {
  return (
    <div
      data-testid={testId}
      className={`relative rounded-3xl border border-[#0F62FE]/15 bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_0_0_1px_rgba(15,98,254,0.05),0_25px_50px_-25px_rgba(15,98,254,0.35)] ${className}`}
    >
      <div
        className={`grid lg:grid-cols-2 gap-8 lg:gap-12 p-6 sm:p-8 lg:p-12 items-center ${
          reverse ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* Text column */}
        <div>
          <div className="flex items-center gap-3">
            <div className="relative w-14 h-14 shrink-0">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-[#0F62FE]/20 blur-xl"
              />
              <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[#4098FF] via-[#0F62FE] to-[#0038B8] flex items-center justify-center shadow-lg ring-4 ring-white">
                <Icon className="w-6 h-6 text-white" strokeWidth={2} />
              </div>
            </div>
            {comingSoonLabel && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
                data-testid={`${testId}-badge`}
              >
                <Sparkles className="w-3 h-3" /> {comingSoonLabel}
              </span>
            )}
          </div>
          <h3
            className="mt-5 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.1]"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {title}
          </h3>
          <p className="mt-3 text-slate-600 text-base sm:text-lg leading-relaxed">
            {description}
          </p>
          {bullets && bullets.length > 0 && (
            <ul className="mt-6 space-y-2.5">
              {bullets.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm sm:text-[15px] text-slate-700"
                >
                  <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#0F62FE]/10 text-[#0F62FE] flex items-center justify-center">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Media column */}
        <div className="relative">{children}</div>
      </div>
    </div>
  );
}


